<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';

import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { useFlowSteps } from '@Use/useFlowSteps';
import { useMppPolicyLabel } from '@Use/useMppPolicyLabel';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import { getInsuranceOptionsApi } from '@Api/requestFlow.api';
import type { MppPolicy } from '@Types/MppPolicy';
import { LINKS } from '@Constants/links.constants';
import { trackMppSkipped } from '@Analytics/request.mixpanel';

// Import used components
import { FlowLayout } from '@Shared';

// Import images
import checkIcon from '@Assets/images/check.svg';
import statesideLogo from '@Assets/images/stateside-logo.svg?inline';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const { advanceFlow, goBackInFlow, skipToNextFlowType, resumeWireFlowStep } = useFlowNavigation();
const { getProgressSteps } = useFlowSteps();
const steps = getProgressSteps();
const mppCurrentStep = steps[0] ?? '';
const { formatPolicyLabel } = useMppPolicyLabel();
const { taskNavTitle } = useTaskNavTitle();

// Insurance options from API
const insuranceOptions = ref<MppPolicy[]>([]);

const transferAmountOptions = computed(() =>
    insuranceOptions.value.map((option) => ({
        id: option.mppPolicyId,
        label: formatPolicyLabel(option),
        policy: option,
    })),
);

const selectedOption = computed({
    get() {
        if (!selectedPolicy.value) return null;
        return (
            transferAmountOptions.value.find(
                (o) => o.id === selectedPolicy.value!.mppPolicyId,
            ) ?? null
        );
    },
    set(option: { id: string; label: string; policy: MppPolicy } | null) {
        selectedPolicy.value = option?.policy ?? null;
        if (option?.policy) {
            requestSessionStore.selectedMppPolicy = option.policy;
        }
    },
});

const formatPolicyCost = (policy: MppPolicy): string | null =>
    policy.cost?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }) ?? null;

const hasSelectedPolicy = computed(() => !!selectedPolicy.value);

// Page state
const isNavigatingYes = ref(false);
const isNavigatingNo = ref(false);
const errorMessage = ref<string | null>(null);
const selectedPolicy = ref<MppPolicy | null>(null);

onMounted(async () => {
    try {
        insuranceOptions.value = await getInsuranceOptionsApi();

        // Restore previously selected policy from the transfer amount page
        if (requestSessionStore.selectedMppPolicy) {
            const restored = insuranceOptions.value.find(
                (p) =>
                    p.mppPolicyId ===
                    requestSessionStore.selectedMppPolicy!.mppPolicyId,
            );
            if (restored) {
                selectedPolicy.value = restored;
            }
        }
    } catch {
        errorMessage.value =
            'Failed to load coverage options. Please refresh and try again.';
    }
});

// Actions
const submitYes = async () => {
    if (isNavigatingYes.value || isNavigatingNo.value || !selectedPolicy.value) return;

    isNavigatingYes.value = true;
    errorMessage.value = null;

    try {
        requestSessionStore.mppPurchaseDecision = true;
        advanceFlow();
    } catch {
        errorMessage.value = 'Something went wrong. Please try again.';
        isNavigatingYes.value = false;
    }
};

const submitNo = async () => {
    if (isNavigatingYes.value || isNavigatingNo.value) return;

    isNavigatingNo.value = true;
    errorMessage.value = null;

    try {
        requestSessionStore.mppPurchaseDecision = false;
        trackMppSkipped();
        if (!resumeWireFlowStep()) {
            skipToNextFlowType();
        }
    } catch {
        errorMessage.value = 'Something went wrong. Please try again.';
        isNavigatingNo.value = false;
    }
};
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="mppCurrentStep"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <app-form-content-layout>
            <h1 bottomOffset="1rem">
                Money Protection Plan
            </h1>

            <p class="mpp-purchase-decision__description">
                Criminals are diverting wire transfers and you may be a target.
                CertifID's <strong>Money Protection Plan</strong> provides the
                highest level of protection against fraud.
            </p>

            <!-- Plan Benefits Section -->
            <div class="mpp-purchase-decision__benefits">
                <div class="mpp-purchase-decision__benefits-header">
                    Your coverage benefits
                </div>

                <div class="mpp-purchase-decision__benefits-content">
                    <div class="mpp-purchase-decision__benefit-section">
                        <h3 class="mpp-purchase-decision__benefit-title">
                            Help recovering your funds
                        </h3>
                        <p class="mpp-purchase-decision__benefit-description">
                            If you get tricked into sending funds to a
                            fraudulent account, our team of experts will:
                        </p>
                        <ul class="mpp-purchase-decision__benefit-list">
                            <li class="mpp-purchase-decision__benefit-item">
                                <img
                                    :src="checkIcon"
                                    alt=""
                                    class="mpp-purchase-decision__check-icon"
                                />
                                <span>
                                    Work with your bank to initiate recovery
                                </span>
                            </li>
                            <li class="mpp-purchase-decision__benefit-item">
                                <img
                                    :src="checkIcon"
                                    alt=""
                                    class="mpp-purchase-decision__check-icon"
                                />
                                <span>Coordinate with law enforcement</span>
                            </li>
                            <li class="mpp-purchase-decision__benefit-item">
                                <img
                                    :src="checkIcon"
                                    alt=""
                                    class="mpp-purchase-decision__check-icon"
                                />
                                <span>
                                    Provide updates and guidance throughout the
                                    recovery process
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div class="mpp-purchase-decision__benefit-section">
                        <h3 class="mpp-purchase-decision__benefit-title">
                            Support on speed dial
                        </h3>
                        <p class="mpp-purchase-decision__benefit-description">
                            Sending a wire transfer can feel complicated. Money
                            Protection Plan customers can call, email, or chat
                            with our team of experts at CertifID.
                        </p>
                    </div>

                    <div class="mpp-purchase-decision__divider"></div>

                    <div class="mpp-purchase-decision__pricing">
                        <div class="mpp-purchase-decision__pricing-item">
                            <span class="mpp-purchase-decision__pricing-label">
                                Protection:
                            </span>
                            <span class="mpp-purchase-decision__pricing-value">
                                {{
                                    selectedPolicy
                                        ? formatPolicyLabel(selectedPolicy)
                                        : "Up to $50,000"
                                }}
                            </span>
                        </div>
                        <div class="mpp-purchase-decision__pricing-item">
                            <span class="mpp-purchase-decision__pricing-label">
                                Price:
                            </span>
                            <span
                                class="mpp-purchase-decision__pricing-value mpp-purchase-decision__pricing-value--highlight"
                            >
                                {{
                                    selectedPolicy
                                        ? formatPolicyCost(selectedPolicy) ?? 'Price unavailable'
                                        : "$20"
                                }}
                            </span>
                        </div>
                    </div>

                    <!-- Adjust Transfer Amount Dropdown -->
                    <app-dropdown
                        v-model="selectedOption"
                        label="Transfer range"
                        empty-text="Select"
                        id-field="id"
                        display-field="label"
                        :options="transferAmountOptions"
                        data-id="mpp-adjust-transfer-amount"
                    />
                </div>
            </div>

            <p
                v-if="errorMessage"
                class="mpp-purchase-decision__error"
                role="alert"
            >
                {{ errorMessage }}
            </p>

            <div class="mpp-purchase-decision__actions">
                <app-button
                    expanded
                    size="large"
                    :is-loading="isNavigatingYes"
                    :is-disabled="isNavigatingYes || isNavigatingNo || !hasSelectedPolicy"
                    data-id="mpp-decision-yes"
                    @onClick="submitYes"
                >
                    Add Money Protection for
                    {{
                        selectedPolicy
                            ? formatPolicyCost(selectedPolicy) ?? 'Price unavailable'
                            : "$20"
                    }}
                </app-button>

                <app-button
                    expanded
                    size="large"
                    type="outline"
                    :is-loading="isNavigatingNo"
                    :is-disabled="isNavigatingYes"
                    data-id="mpp-decision-no"
                    @onClick="submitNo"
                >
                    No, thanks
                </app-button>
            </div>

            <div class="mpp-purchase-decision__legal">
                <div class="mpp-purchase-decision__legal-divider"></div>
                <div class="mpp-purchase-decision__legal-content">
                    <div class="mpp-purchase-decision__legal-first-row">
                        <p class="mpp-purchase-decision__legal-text">
                            This Money Protection Plan is brought to you by
                            CertifID Insurance Services LLC in connection with
                            Stateside Underwriting Agency. For more details on
                            the Money Protection Plan please visit:
                            <app-link
                                :link-url="LINKS.Mpp.LearnMore"
                                type="internal"
                                data-id="mpp-learn-more-link"
                            >
                                {{ LINKS.Mpp.LearnMore }}
                            </app-link>
                        </p>
                        <img
                            :src="statesideLogo"
                            alt="Stateside Underwriting Agency"
                            class="mpp-purchase-decision__legal-logo"
                        />
                    </div>
                    <p class="mpp-purchase-decision__legal-text">
                        You acknowledge that you are at least 18 years of
                        age. You acknowledge that you are sending funds
                        electronically to a third party in connection with a
                        lawful transaction located within the United States.
                    </p>
                </div>
            </div>
        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.mpp-purchase-decision {

    &__description {
        font-size: 1rem;
        line-height: 1.5;
        color: $black-high;
    }

    &__benefits {
        background: $white;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
    }

    &__benefits-header {
        background: var(--ch-primary-dark);
        color: $white;
        font-size: 0.9rem;
        font-weight: 700;
        line-height: 1.5;
        text-align: center;
        padding: 0.75rem;
        border-radius: 0.375rem 0.375rem 0 0;
    }

    &__benefits-content {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    &__benefit-section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    &__benefit-title {
        font-size: 1rem;
        font-weight: 700;
        line-height: 1.5;
        color: $black-med;
        margin: 0;
    }

    &__benefit-description {
        font-size: 1rem;
        line-height: 1.5;
        color: $black-med;
        margin: 0;
    }

    &__benefit-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        line-height: 1.5;
    }

    &__benefit-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1rem;
        line-height: 150%;
        color: $black-med;
    }

    &__check-icon {
        flex-shrink: 0;
        width: 1rem;
        height: 1rem;
    }

    &__divider {
        height: 1px;
        background: $dark-gray;
    }

    &__adjust {
        width: 100%;
    }

    &__pricing {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
    }

    &__pricing-item {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        &:last-child {
            align-items: flex-end;
        }
    }

    &__pricing-label {
        font-size: 0.875rem;
        color: $black-med;
        line-height: 1.5;
    }

    &__pricing-value {
        font-size: 0.875rem;
        font-weight: 700;
        color: $black-high;
        text-align: right;
        line-height: 1.5;

        &--highlight {
            color: $theme-primary;
        }
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        line-height: 1.5;
        margin: 0;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        margin: 0;
    }

    &__legal {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        margin: 0;
    }

    &__legal-divider {
        height: 1px;
        background: $gray;
    }

    &__legal-content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    &__legal-first-row {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 1rem;
        align-items: start;
    }

    &__legal-text {
        font-size: 0.75rem;
        line-height: 1.5;
        color: $black-low;
        margin: 0;

        a {
            color: $black-low;
            text-decoration: underline;

            &:hover {
                color: $theme-primary;
            }
        }
    }

    &__legal-logo {
        width: 64px;
        height: auto;
        flex-shrink: 0;
    }
}
</style>
